import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { CustomerSubscriptionCheckoutContext } from '@/types/customer-subscription-checkout';
import { resolveBillingCycleLabel } from '@/lib/billing-cycle-label';

interface CheckoutSummaryCardProps {
  context: CustomerSubscriptionCheckoutContext;
  features?: string[];
}

export function CheckoutSummaryCard({ context, features = [] }: CheckoutSummaryCardProps) {
  const { t, i18n } = useTranslation();

  const resolvedPrice = (() => {
    if (context.planSummary.formattedPrice) return context.planSummary.formattedPrice;
    if (typeof context.planSummary.price !== 'number') return '—';
    const currency = context.planSummary.currency ?? 'USD';
    try {
      return new Intl.NumberFormat(i18n.language ?? 'en', { style: 'currency', currency }).format(
        context.planSummary.price / 100,
      );
    } catch {
      return `${currency} ${(context.planSummary.price / 100).toFixed(2)}`;
    }
  })();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('customer.planManagement.checkout.summaryTitle')}</CardTitle>
        <CardDescription>{t('customer.planManagement.checkout.summaryDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{t('customer.planManagement.checkout.plan')}</span>
          <span className="font-medium">{context.planSummary.name}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{t('customer.planManagement.checkout.price')}</span>
          <span className="font-medium">{resolvedPrice}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{t('customer.planManagement.checkout.billingCycle')}</span>
          <span className="font-medium">
            {resolveBillingCycleLabel({ cycleName: context.planSummary.billingCycle }, t) ?? '—'}
          </span>
        </div>

        {features.length ? (
          <div className="pt-2">
            <p className="text-xs font-medium text-muted-foreground">
              {t('customer.planManagement.checkout.includedTitle')}
            </p>
            <ul className="mt-3 space-y-2">
              {features.slice(0, 5).map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 text-emerald-600" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            {features.length > 5 ? (
              <p className="mt-2 text-xs text-muted-foreground">
                {t('customer.planManagement.checkout.moreIncluded', { count: features.length - 5 })}
              </p>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
